import { StrictMode, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
import LocaleLayout from './components/LocaleLayout';
import { SpaceLayout } from './components/SpaceLayout';
import AdminLayout from './components/admin/AdminLayout';
import DocsLayout from './components/docs/DocsLayout';
import { ProfileRedirect } from './components/profile/ProfileRedirect';
import { NotificationProvider } from './contexts/NotificationContext';
import { useThemeStore } from './lib/stores/theme';
import { ThemeSync } from './components/ThemeSync';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { Toaster } from '@/components/ui/toaster';
import '@fontsource/geist/latin-400.css';
import '@fontsource/geist/latin-500.css';
import '@fontsource/geist/latin-600.css';
import '@fontsource/geist/latin-700.css';
import '@fontsource/geist-mono/latin-400.css';
import './globals.css';

// ── Lazy load all pages ──────────────────────────────────────
const HomePage                  = lazy(() => import('./pages/HomePage'));
const MarketplacePage          = lazy(() => import('./pages/MarketplacePage'));
const AgentDetailPage          = lazy(() => import('./pages/AgentDetailPage'));
const CartPage                  = lazy(() => import('./pages/CartPage'));
const OrderConfirmPage           = lazy(() => import('./pages/OrderConfirmPage'));
const LoginPage                = lazy(() => import('./pages/LoginPage'));
const RedeemPage                = lazy(() => import('./pages/RedeemPage'));
const ApplyPage                 = lazy(() => import('./pages/ApplyPage'));
const AdminPromoPage            = lazy(() => import('./pages/admin/AdminPromoPage'));
const RegisterPage             = lazy(() => import('./pages/RegisterPage'));
const ProfilePage              = lazy(() => import('./pages/ProfilePage'));
const DocsPage                 = lazy(() => import('./pages/DocsPage'));
const DocsIndexPage            = lazy(() => import('./pages/DocsIndexPage'));
const AboutPage                = lazy(() => import('./pages/AboutPage'));
const PrivacyPage              = lazy(() => import('./pages/PrivacyPage'));
const TermsPage                = lazy(() => import('./pages/TermsPage'));
const ProviderAgreementPage    = lazy(() => import('./pages/ProviderAgreementPage'));
const RefundPolicyPage         = lazy(() => import('./pages/RefundPolicyPage'));
const SupportPage              = lazy(() => import('./pages/SupportPage'));
const ForgotPasswordPage     = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage       = lazy(() => import('./pages/ResetPasswordPage'));
// ── Profile shell (Task 8) ─────────────────────────────────────
const UserProfileLayout    = lazy(() => import('./pages/UserProfileLayout'));
const ProfileAgentsPage    = lazy(() => import('./pages/ProfileAgentsPage'));
const ProfileFollowingPage = lazy(() => import('./pages/ProfileFollowingPage'));
const ProfileFavoritesPage = lazy(() => import('./pages/ProfileFavoritesPage'));
const ProfileFollowersPage = lazy(() => import('./pages/ProfileFollowersPage'));
const DashboardPage            = lazy(() => import('./pages/DashboardPage'));
const OrdersPage               = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage         = lazy(() => import('./pages/OrderDetailPage'));
const SessionsPage            = lazy(() => import('./pages/SessionsPage'));
const SessionDetailPage       = lazy(() => import('./pages/SessionDetailPage'));
const SettingsPage             = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage       = lazy(() => import('./pages/NotificationsPage'));
const ProviderDashboardPage   = lazy(() => import('./pages/ProviderDashboardPage'));
const ProviderAgentsPage     = lazy(() => import('./pages/ProviderAgentsPage'));
const ProviderSessionsPage   = lazy(() => import('./pages/ProviderSessionsPage'));
const ProviderIncomePage      = lazy(() => import('./pages/ProviderIncomePage'));
const ProviderRefundsPage     = lazy(() => import('./pages/ProviderRefundsPage'));
const AgentApplyProviderPage = lazy(() => import('./pages/AgentApplyProviderPage'));
const AgentNewPage            = lazy(() => import('./pages/AgentNewPage'));
const AgentEditPage           = lazy(() => import('./pages/AgentEditPage'));
const AuthCallbackPage        = lazy(() => import('./pages/AuthCallbackPage'));
const DashboardAgentDetailPage = lazy(() => import('./pages/DashboardAgentDetailPage'));
const CapabilitiesPage       = lazy(() => import('./pages/CapabilitiesPage'));
const AdminDashboardPage       = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminAgentsPage         = lazy(() => import('./pages/admin/AdminAgentsPage'));
const AdminAgentDetailPage    = lazy(() => import('./pages/admin/AdminAgentDetailPage'));
const AdminUsersPage          = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUserDetailPage     = lazy(() => import('./pages/admin/AdminUserDetailPage'));
const AdminStaffPage          = lazy(() => import('./pages/admin/AdminStaffPage'));
const AdminStaffDetailPage    = lazy(() => import('./pages/admin/AdminStaffDetailPage'));
const AdminNewStaffPage       = lazy(() => import('./pages/admin/AdminNewStaffPage'));
const AdminTasksPage          = lazy(() => import('./pages/admin/AdminTasksPage'));
const AdminFinancePage        = lazy(() => import('./pages/admin/AdminFinancePage'));
const AdminSessionsPage      = lazy(() => import('./pages/admin/AdminSessionsPage'));
const AdminMechaPage         = lazy(() => import('./pages/admin/AdminMechaPage'));
const AdminWithdrawalsPage   = lazy(() => import('./pages/admin/AdminWithdrawalsPage'));
const AdminRefundsPage      = lazy(() => import('./pages/admin/AdminRefundsPage'));
const AdminAuditPage          = lazy(() => import('./pages/admin/AdminAuditPage'));
const AdminReconciliationPage = lazy(() => import('./pages/admin/AdminReconciliationPage'));
const AdminProposalsPage     = lazy(() => import('./pages/admin/AdminProposalsPage'));
const AdminCapabilitiesPage   = lazy(() => import('./pages/admin/AdminCapabilitiesPage'));
const AdminMcpPermissionsPage = lazy(() => import('./pages/admin/AdminMcpPermissionsPage'));
const AdminDocsPage           = lazy(() => import('./pages/admin/AdminDocsPage'));
const AdminNewDocPage        = lazy(() => import('./pages/admin/AdminNewDocPage'));
const AdminEditDocPage       = lazy(() => import('./pages/admin/AdminEditDocPage'));
const ConsumerAgentsPage    = lazy(() => import('./pages/ConsumerAgentsPage'));
const ProviderAgentNewPage    = lazy(() => import('./pages/ProviderAgentNewPage'));
const DevicesPage           = lazy(() => import('./pages/DevicesPage'));
const DeviceVerifyPage      = lazy(() => import('./pages/DeviceVerifyPage'));
// FavoritesPage / FollowingPage / FollowersPage 旧 dashboard 路由已在 Task 8 删除；
// 文件保留在磁盘作为 Task 12/13 重构蓝图，故不再在此 import。
const WalletPage             = lazy(() => import('./pages/WalletPage'));
const NotFoundPage           = lazy(() => import('./pages/NotFoundPage'));

// ── TanStack Query client ───────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:  5 * 60 * 1000,
      gcTime:    10 * 60 * 1000,
      retry:      1,
    },
  },
});

// ── Route config (data router) ─────────────────────────────────────
const routes = [
  {
    path: '/',
    element: <Navigate to="/en" replace />,
  },
  // 裸路径(无 locale 前缀)落到默认语言——外链/Google consent screen 若填裸路径仍有效
  { path: '/privacy', element: <Navigate to="/en/privacy" replace /> },
  { path: '/terms', element: <Navigate to="/en/terms" replace /> },
  { path: '/refund-policy', element: <Navigate to="/en/refund-policy" replace /> },
  { path: '/support', element: <Navigate to="/en/support" replace /> },
  {
    path: '/:locale',
    element: <LocaleLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true,                    element: <HomePage /> },
      { path: 'about',              element: <AboutPage /> },
      { path: 'privacy',            element: <PrivacyPage /> },
      { path: 'terms',              element: <TermsPage /> },
      { path: 'provider-agreement', element: <ProviderAgreementPage /> },
      { path: 'refund-policy',      element: <RefundPolicyPage /> },
      { path: 'support',            element: <SupportPage /> },
      { path: 'marketplace',            element: <MarketplacePage /> },
      { path: 'marketplace/:slug',     element: <AgentDetailPage /> },
      { path: 'marketplace/create', element: <AgentNewPage /> },
      { path: 'cart',                  element: <CartPage /> },
      { path: 'cart/checkout',       element: <OrderConfirmPage /> },
      { path: 'login',                 element: <LoginPage /> },
      { path: 'redeem',                element: <RedeemPage /> },
      { path: 'apply',                 element: <ApplyPage /> },
      { path: 'devices/verify',        element: <DeviceVerifyPage /> },
      { path: 'auth/callback',         element: <AuthCallbackPage /> },
      { path: 'login/forgot-password', element: <ForgotPasswordPage /> },
      { path: 'login/reset-password', element: <ResetPasswordPage /> },
      { path: 'register',              element: <RegisterPage /> },
      { path: 'profile/*',             element: <ProfilePage /> },
      {
        path: 'docs',
        element: <DocsLayout />,
        children: [
          { index: true, element: <DocsIndexPage /> },
          { path: '*', element: <DocsPage /> },
        ],
      },
      {
        element: <SpaceLayout />,
        children: [
      {
        path: 'users/:id',
        element: <UserProfileLayout />,
        children: [
          { index: true,       element: <ProfileAgentsPage /> },
          { path: 'settings',  element: <SettingsPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'following', element: <ProfileFollowingPage /> },
          { path: 'favorites', element: <ProfileFavoritesPage /> },
          { path: 'followers', element: <ProfileFollowersPage /> },
        ],
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
        children: [
          { index: true,                       element: <Navigate to="sessions" replace /> },
          { path: 'following',                 element: <ProfileRedirect section="following" /> },
          { path: 'favorites',                 element: <ProfileRedirect section="favorites" /> },
          { path: 'provider/followers',        element: <ProfileRedirect section="followers" /> },
          { path: 'agents',                 element: <ConsumerAgentsPage /> },
          { path: 'rented-agents',      element: <Navigate to="agents" replace /> },
          { path: 'orders',                   element: <OrdersPage /> },
          { path: 'orders/:id',               element: <OrderDetailPage /> },
          { path: 'sessions',                 element: <SessionsPage /> },
          { path: 'sessions/:id',            element: <SessionDetailPage /> },
          { path: 'devices',                  element: <DevicesPage /> },
          { path: 'settings',                 element: <ProfileRedirect section="settings" /> },
          { path: 'notifications',          element: <ProfileRedirect section="notifications" /> },
          { path: 'wallet',                   element: <WalletPage /> },
          { path: 'agents/new',                    element: <AgentNewPage /> },
          { path: 'agents/:slug/edit',            element: <AgentEditPage /> },
          { path: 'agents/:slug/apply-provider', element: <AgentApplyProviderPage /> },
          { path: 'agents/:slug',                  element: <DashboardAgentDetailPage /> },
          { path: 'capabilities',            element: <CapabilitiesPage /> },
          { path: 'provider',            element: <ProviderDashboardPage /> },
          { path: 'provider/agents',     element: <ProviderAgentsPage /> },
          { path: 'provider/agents/new', element: <ProviderAgentNewPage /> },
          { path: 'provider/agents/:slug', element: <DashboardAgentDetailPage /> },
          { path: 'provider/agents/:slug/edit', element: <AgentEditPage /> },
          { path: 'provider/sessions',   element: <ProviderSessionsPage /> },
          { path: 'provider/income',     element: <ProviderIncomePage /> },
          { path: 'provider/refunds',    element: <ProviderRefundsPage /> },
          {
            path: 'admin',
            element: <AdminLayout />,
            children: [
              { index: true,              element: <AdminDashboardPage /> },
              { path: 'agents',           element: <AdminAgentsPage /> },
              { path: 'agents/:id',       element: <AdminAgentDetailPage /> },
              { path: 'users',            element: <AdminUsersPage /> },
              { path: 'users/:id',        element: <AdminUserDetailPage /> },
              { path: 'staff',            element: <AdminStaffPage /> },
              { path: 'staff/:id',        element: <AdminStaffDetailPage /> },
              { path: 'staff/new',        element: <AdminNewStaffPage /> },
              { path: 'tasks',            element: <AdminTasksPage /> },
              { path: 'finance',          element: <AdminFinancePage /> },
              { path: 'sessions',        element: <AdminSessionsPage /> },
              { path: 'mecha',           element: <AdminMechaPage /> },
              { path: 'withdrawals',      element: <AdminWithdrawalsPage /> },
              { path: 'refunds',          element: <AdminRefundsPage /> },
              { path: 'audit',            element: <AdminAuditPage /> },
              { path: 'reconciliation',   element: <AdminReconciliationPage /> },
              { path: 'promo',               element: <AdminPromoPage /> },
              { path: 'proposals',       element: <AdminProposalsPage /> },
              { path: 'capabilities',     element: <AdminCapabilitiesPage /> },
              { path: 'mcp-permissions', element: <AdminMcpPermissionsPage /> },
              { path: 'docs',             element: <AdminDocsPage /> },
              { path: 'docs/new',         element: <AdminNewDocPage /> },
              { path: 'docs/:id/edit',   element: <AdminEditDocPage /> },
            ],
          },
        ],
      },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

// 首屏前应用本地主题偏好（防 FOUC）。登录后由 <ThemeSync /> 同步后端偏好。
useThemeStore.getState().initTheme();

const router = createBrowserRouter(routes);

// ── Mount ──────────────────────────────────────────────────────────
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
        <ThemeSync />
        <RouterProvider router={router} />
        <Toaster />
      </NotificationProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
